/**
 * FamilyDashboard — flagship Clipboard+ surface for parents.
 *
 * One screen, every kid, every team, every event in the next three weeks.
 * Three time buckets (Today / This Week / Upcoming) sourced from the
 * single GET /family/dashboard endpoint, with conflict pills flagging any
 * pair of events that overlap on the same day so the user catches
 * double-bookings before they happen.
 *
 * Free guardians get an UpgradeGateCard with the "Family Sports Dashboard"
 * preset — the backend returns `unlocked:false` so we don't have to do
 * separate tier resolution on the client.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, Calendar, CalendarPlus, Copy, Users } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/chalkApi";
import { copyToClipboard } from "../lib/copyToClipboard";
import { formatEventDate, formatEventTime } from "../lib/eventTime";
import { startCheckout } from "../lib/checkout";
import EventWeatherBadge from "../components/chalk/EventWeatherBadge";
import ManualTeamsCard from "../components/chalk/ManualTeamsCard";
import {
  BottomSheet, ChalkActionButton, ChalkCard, DarkHeader, EmptyState,
  SectionTitle, UpgradeGateCard, WhiteHeader,
} from "../components/chalk/primitives";
import { UPGRADE_GATES } from "../lib/upgradeGates";

const SPORT_GLYPH = {
  baseball: "⚾", softball: "🥎", soccer: "⚽",
  basketball: "🏀", football: "🏈", other: "🏆",
};

function _fmtDay(event, user) {
  // Timezone-aware day label. Uses the team's TZ (or the user's override
  // if set in Settings) so the grouping aligns with how everyone else on
  // the team sees the schedule.
  return formatEventDate(event, user);
}

function _fmtTime(event, user) {
  return formatEventTime(event, user);
}

function _groupByDay(rows) {
  const out = new Map();
  for (const r of rows) {
    const key = (r.start_local || "").slice(0, 10);
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(r);
  }
  return Array.from(out.entries()); // [[YYYY-MM-DD, rows], ...] preserves order
}

export default function FamilyDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [calLink, setCalLink] = useState(null);
  const [fetchingLink, setFetchingLink] = useState(false);

  // `reload` exists so child cards (eg ManualTeamsCard) can refresh the
  // dashboard after the user adds/edits/removes a manual entry without
  // either us prop-drilling individual setters or them re-fetching the
  // top-level shape themselves.
  async function reload() {
    try {
      const d = await api.familyDashboard();
      setData(d);
    } catch { /* leave existing state on transient failure */ }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const d = await api.familyDashboard();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) toast.error(e.message || "Couldn't load your family dashboard.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  async function upgrade() {
    setUpgrading(true);
    try {
      await startCheckout({
        tier: "clipboard_plus",
        context: { feature_name: "FAMILY_DASHBOARD", source_screen: "family_dashboard", current_tier: "pickup" },
      });
    } catch (e) {
      toast.error(e.message || "Couldn't open checkout.");
      setUpgrading(false);
    }
  }

  async function openSubscribe() {
    setSubscribeOpen(true);
    if (calLink) return;
    setFetchingLink(true);
    try {
      const d = await api.familyCalendarToken();
      // Backend returns paths only — resolve against the live origin so the
      // copyable URL works no matter which env the user is on.
      const origin = window.location.origin;
      const httpsAbs = d.https_url.startsWith("http") ? d.https_url : `${origin}${d.https_url}`;
      const webcalAbs = d.webcal_url.replace("__HOST__", window.location.host);
      setCalLink({ httpsUrl: httpsAbs, webcalUrl: webcalAbs, token: d.token });
    } catch (e) {
      toast.error(e.message || "Couldn't generate your calendar link.");
    } finally {
      setFetchingLink(false);
    }
  }

  async function copy(label, value) {
    const ok = await copyToClipboard(value);
    if (ok) toast.success(`${label} copied.`);
    else toast.error("Copy failed — long-press to select.");
  }

  const Header = WhiteHeader; // parent-side surface — light, calm
  const summary = data?.summary || {};

  return (
    <>
      <Header
        eyebrow="The Clipboard"
        title="Family Sports Dashboard"
        subtitle={user.first_name ? `Hi, ${user.first_name}.` : ""}
      />
      <div style={{ padding: "0 18px 110px" }}>
        {loading ? (
          <div style={{ padding: "32px 8px", textAlign: "center", color: "var(--chalk-muted)" }}>
            Loading every kid, every team…
          </div>
        ) : !data?.unlocked ? (
          <UpgradeGateCard
            featureName={UPGRADE_GATES.family_dashboard.featureName}
            benefit={UPGRADE_GATES.family_dashboard.benefit}
            target={UPGRADE_GATES.family_dashboard.target}
            ctaLabel={upgrading ? "Opening…" : "Upgrade to The Clipboard+"}
            onUpgrade={upgrade}
            data-testid="family-dashboard-upgrade"
          />
        ) : summary.events === 0 ? (
          <>
            <SubscribeCalendarCard onOpen={openSubscribe} />
            <ManualTeamsCard onChanged={reload} />
            <EmptyState
              icon={<Calendar size={42} aria-hidden="true" />}
              headline="Nothing on the schedule yet."
              body="Once a coach adds plays, every game and practice for every kid lands here. Or add a team yourself above."
            />
          </>
        ) : (
          <>
            <SummaryStrip summary={summary} />
            <SubscribeCalendarCard onOpen={openSubscribe} />
            <ManualTeamsCard onChanged={reload} />
            <Bucket
              testId="bucket-today"
              label="Today"
              rows={data.buckets.today}
              emptyText="Nothing on the slate today. Enjoy it."
              groupByDay={false}
              user={user}
            />
            <Bucket
              testId="bucket-this-week"
              label="This Week"
              rows={data.buckets.this_week}
              emptyText="No plays this week."
              groupByDay
              user={user}
            />
            <Bucket
              testId="bucket-upcoming"
              label="Upcoming"
              rows={data.buckets.upcoming}
              emptyText="Nothing further out yet."
              groupByDay
              user={user}
            />
          </>
        )}
      </div>
      <SubscribeSheet
        isOpen={subscribeOpen}
        loading={fetchingLink}
        link={calLink}
        onCopy={copy}
        onDismiss={() => setSubscribeOpen(false)}
      />
    </>
  );
}

function SubscribeCalendarCard({ onOpen }) {
  return (
    <ChalkCard
      data-testid="family-subscribe-card"
      onClick={onOpen}
      style={{
        cursor: "pointer", marginBottom: 14,
        background: "linear-gradient(135deg, rgba(46,125,82,0.06), rgba(46,125,82,0.02))",
        borderLeft: "2px solid var(--green-mid)",
        display: "flex", alignItems: "center", gap: 12,
      }}
    >
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: "rgba(46,125,82,0.12)", color: "var(--green-mid)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <CalendarPlus size={18} aria-hidden="true" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "var(--chalk-board)", fontWeight: 600 }}>
          Subscribe to your family's schedule
        </div>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 4, lineHeight: 1.4 }}>
          Pull every kid, every team into Apple Calendar / Google Calendar.
          Auto-updates when coaches change anything.
        </div>
      </div>
    </ChalkCard>
  );
}

function SubscribeSheet({ isOpen, loading, link, onCopy, onDismiss }) {
  const googleAddUrl = link
    ? `https://www.google.com/calendar/render?cid=${encodeURIComponent(link.httpsUrl)}`
    : null;
  return (
    <BottomSheet isOpen={isOpen} title="Subscribe to your family calendar" onDismiss={onDismiss}>
      <div data-testid="family-subscribe-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <p style={{ margin: 0, fontSize: 13, color: "var(--chalk-muted)", lineHeight: 1.5 }}>
          A live feed of every kid's events across every team. Calendar apps
          re-fetch every hour, so coach changes flow to your phone
          automatically.
        </p>
        {loading || !link ? (
          <div style={{ padding: "12px 4px", color: "var(--chalk-muted)", fontSize: 13 }}>
            Generating your private link…
          </div>
        ) : (
          <>
            <LinkBlock
              label="Apple Calendar (iPhone / iPad / Mac)"
              caption="Tap to open Calendar with a Subscribe prompt."
              href={link.webcalUrl}
              testId="family-subscribe-ios"
            />
            <LinkBlock
              label="Google Calendar"
              caption="Opens Google Calendar's Add-by-URL flow."
              href={googleAddUrl}
              testId="family-subscribe-google"
              external
            />
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>
                Or copy the URL
              </div>
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                background: "#FBF8F2",
                border: "0.5px solid var(--chalk-border)",
                borderRadius: 10, padding: "10px 12px",
              }}>
                <code style={{
                  flex: 1, fontSize: 12, color: "var(--chalk-board)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {link.httpsUrl}
                </code>
                <button
                  type="button"
                  onClick={() => onCopy("Calendar URL", link.httpsUrl)}
                  data-testid="family-subscribe-copy"
                  aria-label="Copy"
                  style={{
                    display: "inline-flex", alignItems: "center",
                    padding: "6px 10px", borderRadius: 8,
                    background: "var(--chalk-board)", color: "#fff", border: "none",
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
                    textTransform: "uppercase", cursor: "pointer", gap: 4,
                  }}
                >
                  <Copy size={12} aria-hidden="true" /> Copy
                </button>
              </div>
              <div style={{ fontSize: 11, color: "var(--chalk-muted)", marginTop: 8, lineHeight: 1.4 }}>
                Keep this link private — anyone with it can read your family's schedule.
              </div>
            </div>
          </>
        )}
        <ChalkActionButton
          testId="family-subscribe-done"
          variant="ghost"
          label="Done"
          onClick={onDismiss}
        />
      </div>
    </BottomSheet>
  );
}

function LinkBlock({ label, caption, href, testId, external }) {
  return (
    <a
      href={href}
      data-testid={testId}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "12px 14px", borderRadius: 10,
        background: "#fff", color: "var(--chalk-board)",
        border: "0.5px solid var(--chalk-border)",
        textDecoration: "none",
      }}
    >
      <div style={{
        width: 30, height: 30, borderRadius: 8,
        background: "rgba(46,125,82,0.10)", color: "var(--green-mid)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        <CalendarPlus size={16} aria-hidden="true" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
          {caption}
        </div>
      </div>
    </a>
  );
}

function SummaryStrip({ summary }) {
  return (
    <ChalkCard data-testid="family-dashboard-summary" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 12, color: "var(--chalk-muted)" }}>
        <Pill icon={<Users size={13} aria-hidden="true" />} label={`${summary.athletes} kid${summary.athletes === 1 ? "" : "s"}`} />
        <Pill icon={<Calendar size={13} aria-hidden="true" />} label={`${summary.teams} team${summary.teams === 1 ? "" : "s"}`} />
        <Pill label={`${summary.events} event${summary.events === 1 ? "" : "s"}`} />
        {summary.conflicts > 0 && (
          <Pill
            icon={<AlertTriangle size={13} aria-hidden="true" />}
            label={`${summary.conflicts} conflict${summary.conflicts === 1 ? "" : "s"}`}
            tone="warn"
            testId="family-dashboard-conflict-summary"
          />
        )}
      </div>
    </ChalkCard>
  );
}

function Pill({ icon, label, tone = "neutral", testId }) {
  const palette = tone === "warn"
    ? { bg: "rgba(176,32,32,0.10)", fg: "#B02020" }
    : { bg: "rgba(46,125,82,0.10)", fg: "var(--green-mid)" };
  return (
    <span
      data-testid={testId}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "5px 10px", borderRadius: 999,
        background: palette.bg, color: palette.fg,
        fontWeight: 700, letterSpacing: "0.04em",
      }}
    >
      {icon}
      {label}
    </span>
  );
}

function Bucket({ label, rows, emptyText, groupByDay, testId, user }) {
  if (!rows) return null;
  return (
    <div data-testid={testId} style={{ marginBottom: 18 }}>
      <SectionTitle>{label}</SectionTitle>
      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: "var(--chalk-muted)", padding: "8px 4px 4px" }}>
          {emptyText}
        </div>
      ) : groupByDay ? (
        _groupByDay(rows).map(([dayKey, dayRows]) => (
          <DayGroup key={dayKey} dayKey={dayKey} rows={dayRows} user={user} />
        ))
      ) : (
        rows.map((r) => <EventCard key={r.id} row={r} user={user} />)
      )}
    </div>
  );
}

function DayGroup({ dayKey, rows, user }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em",
        color: "var(--chalk-muted)", fontWeight: 700, margin: "10px 4px 6px",
      }}>
        {_fmtDay(rows[0], user)}
      </div>
      {rows.map((r) => <EventCard key={r.id} row={r} user={user} />)}
    </div>
  );
}

function EventCard({ row, user }) {
  const glyph = SPORT_GLYPH[row.sport] || SPORT_GLYPH.other;
  return (
    <ChalkCard
      data-testid={`family-event-${row.id}`}
      style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
        borderLeft: `2px solid ${row.team_color || "#2E7D52"}`,
      }}
    >
      <div style={{
        width: 42, fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)",
        fontSize: 22, letterSpacing: "0.04em", color: "var(--chalk-board)",
        textAlign: "center",
      }}>
        {_fmtTime(row, user).replace(" ", "")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 15 }} aria-hidden="true">{glyph}</span>
          <span style={{
            fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em",
            fontWeight: 700, color: "var(--chalk-muted)",
          }}>
            {row.sport} · {row.event_type}
          </span>
          {row.has_conflict && (
            <span
              data-testid={`family-event-${row.id}-conflict`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 3,
                fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
                background: "rgba(176,32,32,0.10)", color: "#B02020",
                padding: "2px 6px", borderRadius: 4, textTransform: "uppercase",
              }}
            >
              <AlertTriangle size={10} aria-hidden="true" /> Conflict
            </span>
          )}
        </div>
        <div style={{
          fontSize: 15, color: "var(--chalk-board)", fontWeight: 600,
          marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {row.athletes.map((a) => a.first_name).join(" & ") || row.title}
        </div>
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 2 }}>
          {row.team_name}{row.location ? ` · ${row.location}` : ""}
        </div>
        <div style={{ marginTop: 6 }}>
          <EventWeatherBadge eventId={row.id} />
        </div>
      </div>
    </ChalkCard>
  );
}
